// exportIncomeMonthlyToExcel.ts
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import moment from 'moment'
import { dateToView } from 'utils/date.utils'
import { forceTextOnDateCells } from 'utils/excel.utils'

export interface IncomeRow {
    paidDate: string   // "YYYY-MM-DD"
    countPaid: number
    countPaidCancel: number
    cancelPaid: number
    countPaidNormal: number
    receiveAdvance: number
    cashPaid: number
    transferPaid: number
    creditPaid: number
    debitPaid: number
    advancePaid: number
    treamentRightPaid: number
    voucherPaid?: number
    promotionPaid?: number
    totalNoAdvancePaid: number
    overDue: number
}

export interface IncomeMonthlyReport {
    branchName: string   // ชื่อสาขา
    monthLabel: string   // e.g. "สิงหาคม 2568"
    prefix: string   // ประเภทใบเสร็จ
    rows: IncomeRow[]
    includeCrm: boolean  // แสดง Gift Voucher/Promotion ไหม
    isDecrease: boolean
    t: (key: string) => string
}

export async function exportIncomeMonthlyToExcel(rpt: IncomeMonthlyReport) {
    const wb = new ExcelJS.Workbook()
    const ws: any = wb.addWorksheet(rpt.t('REPORT.REPORT_NAME.INCOME_SIMPLE_MONTHLY'), {
        pageSetup: { orientation: 'landscape' }
    })
    ws.properties.defaultRowHeight = 18

    // --- คอลัมน์ A–O ---
    const baseCols: Partial<ExcelJS.Column>[] = [
        { header: rpt.t('REPORT.TABLE.INCOME_MONTH.DATE'), width: 12 },
        { header: rpt.t('REPORT.TABLE.INCOME_MONTH.QTY_RECEIPT'), width: 12 },
        { header: rpt.t('REPORT.TABLE.INCOME_MONTH.QTY_RECEIPT_CANCEL'), width: 12 },
        { header: rpt.t('REPORT.TABLE.INCOME_MONTH.NET_CANCEL'), width: 12 },
        { header: rpt.t('REPORT.TABLE.INCOME_MONTH.QTY_RECEIPT_NORMAL'), width: 12 },
        { header: rpt.isDecrease ? rpt.t('REPORT.TABLE.INCOME_MONTH.ADVANCE_INCOME_NOT_CAL') : rpt.t('REPORT.TABLE.INCOME_MONTH.ADVANCE_INCOME'), width: 16 },
        { header: rpt.t('REPORT.TABLE.INCOME_MONTH.CASH'), width: 12 },
        { header: rpt.t('REPORT.TABLE.INCOME_MONTH.TRANSFER'), width: 12 },
        { header: rpt.t('REPORT.TABLE.INCOME_MONTH.CREDIT'), width: 12 },
        { header: rpt.t('REPORT.TABLE.INCOME_MONTH.DEBIT'), width: 12 },
        { header: rpt.isDecrease ? rpt.t('REPORT.TABLE.INCOME_MONTH.ADVANCE_DECREASE') : rpt.t('REPORT.TABLE.INCOME_MONTH.ADVANCE_DECREASE_NOT_CAL'), width: 16 },
        { header: rpt.t('REPORT.TABLE.INCOME_MONTH.TREATMENT_RIGHT'), width: 12 },
    ]
    const crmCols = rpt.includeCrm
        ? [
            { header: rpt.t('REPORT.TABLE.INCOME_MONTH.GIFT_VOUCHER'), width: 12 },
            { header: rpt.t('REPORT.TABLE.INCOME_MONTH.PROMOTION'), width: 12 },
            { header: rpt.t('REPORT.TABLE.INCOME_MONTH.TOTAL'), width: 12 },
            { header: rpt.t('REPORT.TABLE.INCOME_MONTH.OVERDUE'), width: 12 }
        ]
        : [
            { header: rpt.t('REPORT.TABLE.INCOME_MONTH.TOTAL'), width: 12 },
            { header: rpt.t('REPORT.TABLE.INCOME_MONTH.OVERDUE'), width: 12 }
        ]

    ws.columns = [...baseCols, ...crmCols] as ExcelJS.Column[]

    let r = 1

    // 1) Title
    ws.mergeCells(r, 1, r, ws.columns.length)
    ws.getCell(r, 1).value = rpt.t('REPORT.REPORT_NAME.INCOME_SIMPLE_MONTHLY')
    ws.getCell(r, 1).font = { size: 16, bold: true }
    ws.getCell(r, 1).alignment = { horizontal: 'center' }
    r++

    // 2) Filter row
    ws.mergeCells(r, 1, r, ws.columns.length)
    ws.getCell(r, 1).value = [
        `${rpt.t('REPORT.FILTER.BRANCH')}: ${rpt.branchName}`,
        `${rpt.t('REPORT.FILTER.MONTH')}: ${rpt.monthLabel}`,
        `${rpt.t('REPORT.FILTER.RECEIPT_TYPE')}: ${rpt.prefix === undefined ? rpt.t(`REPORT.FILTER.ALL`) : rpt.prefix}`
    ].join('    ')
    ws.getCell(r, 1).font = { size: 12 }
    ws.getCell(r, 1).alignment = { horizontal: 'left' }
    r += 2

    // 3) Header ตารางรายละเอียด (row r)
    const header1 = ws.addRow(ws.columns.map((c: any) => (c.header as string)))
    header1.font = { bold: true }
    header1.alignment = { vertical: 'middle' }
    header1.height = 20
    header1.eachCell((cell: any, colNumber: any) => {
        cell.border = {
            top: { style: 'thin' }, left: { style: 'thin' },
            bottom: { style: 'thin' }, right: { style: 'thin' }
        }
        if (colNumber === 1) {
            cell.alignment = { horizontal: 'left', vertical: 'middle' };
        } else {
            cell.alignment = { horizontal: 'right', vertical: 'middle' };
        }
    })
    r++

    // 4) Data rows รายวัน
    rpt.rows.forEach(d => {
        const vals: any[] = [
            // moment(d.paidDate).format('DD/MM/YYYY'),
            dateToView(d.paidDate) || "-",
            d.countPaid,
            d.countPaidCancel,
            d.cancelPaid,
            d.countPaidNormal,
            d.receiveAdvance,
            d.cashPaid,
            d.transferPaid,
            d.creditPaid,
            d.debitPaid,
            d.advancePaid,
            d.treamentRightPaid,
        ]
        if (rpt.includeCrm) {
            vals.push(d.voucherPaid || 0, d.promotionPaid || 0)
        }
        const totalAll = d.totalNoAdvancePaid + (rpt.includeCrm ? (d.voucherPaid || 0) + (d.promotionPaid || 0) : 0)
        vals.push(totalAll, d.overDue)

        const row = ws.addRow(vals)
        row.alignment = { vertical: 'middle' }
        row.eachCell((cell: any, col: any) => {
            cell.border = {
                top: { style: 'thin' }, left: { style: 'thin' },
                bottom: { style: 'thin' }, right: { style: 'thin' }
            }
            // ตัวเลขจัดขวา
            if (col > 1) {
                cell.alignment = { horizontal: 'right', vertical: 'middle' }
                // จำนวนใบเสร็จ, ใบเสร็จยกเลิก, ใบเสร็จปกติ format no decimal
                if ([2, 3, 5].includes(col)) cell.numFmt = '#,##0'
                else cell.numFmt = '#,##0.00'
            }
        })
    })

    // 5) Summary row ตารางแรก
    const sums = rpt.rows.reduce((acc, d) => {
        acc.countPaid += Number(d.countPaid)
        acc.countPaidCancel += Number(d.countPaidCancel)
        acc.cancelPaid += Number(d.cancelPaid)
        acc.countPaidNormal += Number(d.countPaidNormal)
        acc.receiveAdvance += Number(d.receiveAdvance)
        acc.cashPaid += d.cashPaid
        acc.transferPaid += d.transferPaid
        acc.creditPaid += d.creditPaid
        acc.debitPaid += d.debitPaid
        acc.advancePaid += d.advancePaid
        acc.treatmentRight += d.treamentRightPaid
        if (rpt.includeCrm) {
            acc.voucherPaid += d.voucherPaid || 0
            acc.promotionPaid += d.promotionPaid || 0
        }
        acc.totalAll += d.totalNoAdvancePaid + (rpt.includeCrm ? (d.voucherPaid || 0) + (d.promotionPaid || 0) : 0)
        acc.overDue += d.overDue
        return acc
    }, {
        countPaid: 0, countPaidCancel: 0, cancelPaid: 0, countPaidNormal: 0,
        receiveAdvance: 0, cashPaid: 0, transferPaid: 0, creditPaid: 0, debitPaid: 0,
        advancePaid: 0, treatmentRight: 0, voucherPaid: 0, promotionPaid: 0,
        totalAll: 0, overDue: 0
    } as any)

    const footer1: any[] = [
        rpt.t('REPORT.TITLE.SUM'),
        sums.countPaid,
        sums.countPaidCancel,
        sums.cancelPaid,
        sums.countPaidNormal,
        sums.receiveAdvance,
        sums.cashPaid,
        sums.transferPaid,
        sums.creditPaid,
        sums.debitPaid,
        sums.advancePaid,
        sums.treatmentRight,
    ]
    if (rpt.includeCrm) footer1.push(sums.voucherPaid, sums.promotionPaid)
    footer1.push(sums.totalAll, sums.overDue)

    const sumRow1 = ws.addRow(footer1)
    sumRow1.font = { bold: true }
    sumRow1.alignment = { vertical: 'middle' }
    sumRow1.eachCell((cell: any, col: any) => {
        cell.border = {
            top: { style: 'thin' }, left: { style: 'thin' },
            bottom: { style: 'thin' }, right: { style: 'thin' }
        }
        if (col > 1) {
            if ([2, 3, 5].includes(col)) { cell.numFmt = '#,##0'; cell.alignment = { horizontal: 'right', vertical: 'middle' } }
            else { cell.numFmt = '#,##0.00'; cell.alignment = { horizontal: 'right', vertical: 'middle' } }
        }
    })

    // … หลังจาก step 6 เว้นบรรทัดว่าง …
    r = ws.lastRow.number + 2

    // 7) ชื่อหัวตารางสรุปรายได้ (merge ไปถึงท้ายเหมือนเดิม)
    ws.mergeCells(r, 1, r, ws.columns.length)
    const titleRow2 = ws.getRow(r)
    titleRow2.getCell(1).value = rpt.t('REPORT.TITLE.INCOME_SIMPLE_MONTHLY_TOTAL')
    titleRow2.getCell(1).font = { bold: true }
    titleRow2.getCell(1).alignment = { horizontal: 'left' }
    r += 2

    // 8) Header ตารางสรุปรายเดือน (ข้ามคอลัมน์แรก)
    const headers2 = ws.columns
        .slice(1)                       // ตัดคอลัมน์แรก (วันที่) ออก
        .map((c: any) => c.header as string)   // เอา header ของแต่ละ col
    const hdr2 = ws.addRow(headers2)
    hdr2.font = { bold: true }
    hdr2.alignment = { vertical: 'middle' }
    hdr2.height = 20
    hdr2.eachCell((cell: any) => {
        cell.border = {
            top: { style: 'thin' }, left: { style: 'thin' },
            bottom: { style: 'thin' }, right: { style: 'thin' }
        }
        cell.alignment = { horizontal: 'right', vertical: 'middle' };
    })
    r++

    // 9) Summary data (ใช้ footer1 แต่ข้ามค่าตัวแรกด้วย slice)
    const data2 = footer1.slice(1)   // footer1[0] คือ "รวม" คอลัมน์วันที่ ให้ข้าม
    const sumRow2 = ws.addRow(data2)
    sumRow2.font = { bold: true }
    sumRow2.alignment = { vertical: 'middle' }
    sumRow2.eachCell((cell: any, col: any) => {
        cell.border = {
            top: { style: 'thin' }, left: { style: 'thin' },
            bottom: { style: 'thin' }, right: { style: 'thin' }
        }
        // col 1 ตอนนี้คือเดิม col2 => จำนวนใบเสร็จ
        if ([1, 2, 4].includes(col)) { // 1=>countPaid,2=>countPaidCancel,4=>countPaidNormal
            cell.numFmt = '#,##0'
            cell.alignment = { horizontal: 'right', vertical: 'middle' }
        }
        else if (col > 0) {
            cell.numFmt = '#,##0.00'
            cell.alignment = { horizontal: 'right', vertical: 'middle' }
        }
    })


    // 10) Save
    forceTextOnDateCells(wb)
    const buf = await wb.xlsx.writeBuffer()
    saveAs(new Blob([buf]), `${rpt.t('REPORT.REPORT_NAME.INCOME_SIMPLE_MONTHLY')}_${moment().format('YYYYMM')}.xlsx`)
}
